import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { Profile, Obra, Company, CompanyLimits } from '@/lib/types'
import { readImpersonationCompanyId } from '@/contexts/AdminImpersonationContext'

/**
 * Resolve o company_id efetivo: se o Super Admin está impersonando uma empresa,
 * usa o ID impersonado; caso contrário, usa o do próprio perfil.
 */
function resolveEffectiveCompanyId(profileCompanyId?: string | null): string | null {
  const impersonated = readImpersonationCompanyId()
  if (impersonated) return impersonated
  return profileCompanyId || null
}

interface AuthContextType {
  user: Profile | null
  loading: boolean
  blocked: boolean
  obras: Obra[]
  obraAtiva: Obra | null
  company: Company | null
  companyLimits: CompanyLimits | null
  setObraAtiva: (obra: Obra) => void
  loadObras: () => Promise<void>
  loadCompany: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [blocked, setBlocked] = useState(false)
  const [obras, setObras] = useState<Obra[]>([])
  const [obraAtiva, setObraAtivaState] = useState<Obra | null>(null)
  const [company, setCompany] = useState<Company | null>(null)
  const [companyLimits, setCompanyLimits] = useState<CompanyLimits | null>(null)

  const loadProfile = useCallback(async (authUser: { id: string; email?: string }): Promise<Profile | null> => {
    let data: any = null
    const { data: d1, error } = await supabase
      .from('profiles')
      .select('id,nome,cargo,perfil,status,modulos_ativos,company_id,company_role')
      .eq('id', authUser.id)
      .single()

    if (error && error.code === '42703') {
      // Fallback if new columns don't exist yet
      const { data: d2 } = await supabase
        .from('profiles')
        .select('id,nome,cargo,perfil,status')
        .eq('id', authUser.id)
        .single()
      data = d2 ? { ...d2, modulos_ativos: [], company_id: null, company_role: 'viewer' } : null
    } else {
      data = d1
    }

    if (!data) {
      console.error('[AuthContext] Perfil não encontrado para', authUser.id)
      // Profile not found — may be pending trigger creation during onboarding.
      // Sign out to avoid inconsistent state; user can retry login.
      await supabase.auth.signOut()
      return null
    }

    if (data.status && data.status !== 'aprovado') {
      await supabase.auth.signOut()
      return null
    }

    // Bloqueia acesso se a empresa estiver inativa (aguardando aprovação)
    if (data.company_id) {
      const { data: companyCheck } = await supabase
        .from('companies')
        .select('is_active')
        .eq('id', data.company_id)
        .single()
      if (companyCheck && companyCheck.is_active === false) {
        await supabase.auth.signOut()
        if (typeof window !== 'undefined') {
          try {
            sessionStorage.setItem('cz:auth_error', 'Sua empresa está aguardando aprovação. Entre em contato com o administrador.')
          } catch { /* noop */ }
        }
        return null
      }
    }

    const profile: Profile = { ...data, email: authUser.email, modulos_ativos: data.modulos_ativos || [] }
    setUser(profile)
    return profile
  }, [])

  const loadCompanyInternal = useCallback(async (companyId?: string) => {
    const cid = companyId || resolveEffectiveCompanyId(user?.company_id)
    if (!cid) { setCompany(null); setCompanyLimits(null); return }
    
    const [{ data: companyData }, { data: limits }] = await Promise.all([
      supabase.from('companies').select('*').eq('id', cid).single(),
      supabase.rpc('check_company_limits', { p_company_id: cid }),
    ])
    
    if (companyData) {
      console.log('[AuthContext] company loaded, logo_url:', companyData.logo_url)
      setCompany(companyData as Company)
    }
    if (limits) setCompanyLimits(limits as CompanyLimits)
  }, [user?.company_id])

  const loadCompany = useCallback(async () => {
    await loadCompanyInternal()
  }, [loadCompanyInternal])

  const loadObrasInternal = useCallback(async (companyId?: string): Promise<Obra[] | null> => {
    const cid = companyId || resolveEffectiveCompanyId(user?.company_id)
    const cacheKey = `construzilla_cache_obras_${cid || 'all'}`

    let query = supabase
      .from('vw_resumo_obras')
      .select('*')
      .order('nome')

    // Defense-in-depth: always filter by company_id on the frontend too
    if (cid) {
      query = query.eq('company_id', cid)
    }

    try {
      const { data, error } = await query
      if (error) throw error
      if (data) {
        setObras(data as Obra[])
        // Salva snapshot para leitura offline
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }))
        } catch {}
      }
      return data as Obra[] | null
    } catch (e) {
      // Offline / falha de rede — tenta cache
      try {
        const cached = localStorage.getItem(cacheKey)
        if (cached) {
          const parsed = JSON.parse(cached) as { data: Obra[]; timestamp: number }
          if (parsed.data) {
            setObras(parsed.data)
            console.warn('[AuthContext] Carregando obras do cache offline')
            return parsed.data
          }
        }
      } catch {}
      return null
    }
  }, [user?.company_id])

  const loadObras = useCallback(async () => {
    await loadObrasInternal()
  }, [loadObrasInternal])

  const setObraAtiva = useCallback((obra: Obra) => {
    setObraAtivaState(obra)
    localStorage.setItem('cpro_ativa', obra.id)
    try { localStorage.setItem('cz:obra_id', obra.id) } catch { /* noop */ }
  }, [])

  // Sincroniza company_id ativo no localStorage para systemLogger
  // Considera o impersonation (Super Admin "Entrar como empresa")
  useEffect(() => {
    try {
      const effective = resolveEffectiveCompanyId(user?.company_id)
      if (effective) localStorage.setItem('cz:company_id', effective)
      else localStorage.removeItem('cz:company_id')
    } catch { /* noop */ }
  }, [user?.company_id])

  const logout = useCallback(async () => {
    await supabase.auth.signOut()
    setUser(null)
    setObras([])
    setObraAtivaState(null)
    setCompany(null)
    setCompanyLimits(null)
    try {
      localStorage.removeItem('cz:company_id')
      localStorage.removeItem('cz:obra_id')
    } catch { /* noop */ }
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const profile = await loadProfile(session.user)
        if (!profile) {
          setBlocked(true)
          setLoading(false)
          return
        }
        // Load company if user has one
        if (profile.company_id) {
          await loadCompanyInternal(profile.company_id)
        }
        const obrasList = await loadObrasInternal(profile.company_id || undefined)
        const saved = localStorage.getItem('cpro_ativa')
        if (obrasList?.length) {
          const toAct = (saved && obrasList.find((o: Obra) => o.id === saved)) || obrasList[0]
          if (toAct) setObraAtiva(toAct)
        }
      }
      setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setUser(null)
        setObras([])
        setObraAtivaState(null)
        setCompany(null)
        setCompanyLimits(null)
      }
    })

    return () => subscription.unsubscribe()
  }, [loadProfile, loadObrasInternal, loadCompanyInternal, setObraAtiva])

  return (
    <AuthContext.Provider value={{ user, loading, blocked, obras, obraAtiva, company, companyLimits, setObraAtiva, loadObras, loadCompany, logout }}>
      {children}
    </AuthContext.Provider>
  )
}
